import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Modal, Spin, message } from "antd";
import { IBusinessFull } from "../../../../interfaces/businessFull.interface";
import {
  Shift4SubmitError,
  submitBusinessToShift4Server,
} from "../../businessesApi";
import styles from "./Shift4SubmitModal.module.css";

interface Shift4SubmitModalProps {
  visible: boolean;
  business: IBusinessFull | null;
  onClose: () => void;
  onSubmitted?: (businessId: string) => void;
}

type Step = "confirm" | "loading" | "success" | "error";

// Shift4's own classification: a 503 is the vendor being unreachable and is
// worth retrying as-is; the rest are verdicts on this merchant's data.
const RETRYABLE_CODES = new Set([
  "SHIFT4_UPSTREAM_UNAVAILABLE",
  "SHIFT4_NETWORK_ERROR",
]);

function errorCopy(code?: string): { title: string; nextStep: string } {
  switch (code) {
    case "SHIFT4_ALREADY_SUBMITTED":
      return {
        title: "Already submitted to Shift4",
        nextStep:
          "Nothing was sent this time. The existing submission is still in flight or already complete — do not submit again.",
      };
    case "SHIFT4_MISCONFIGURED":
      return {
        title: "Shift4 is not configured on this stage",
        nextStep:
          "This is an environment problem, not a problem with the merchant. Ask engineering to wire Shift4 credentials for this stage, then retry.",
      };
    case "SHIFT4_UPSTREAM_UNAVAILABLE":
      return {
        title: "Shift4 could not be reached",
        nextStep:
          "The merchant was not created. Wait a few minutes and try again. If it keeps failing, Shift4 itself is down.",
      };
    case "SHIFT4_REQUEST_REJECTED":
      return {
        title: "Shift4 rejected the merchant",
        nextStep:
          "Fix the fields named in the details below on the merchant profile, then submit again.",
      };
    case "SHIFT4_NETWORK_ERROR":
      return {
        title: "The request did not go out",
        nextStep:
          "The browser never reached our API (blocked request, offline, or a dropped connection). Nothing was sent to Shift4. Unblock the call if you are filtering it, then try again.",
      };
    default:
      return {
        title: "Submission to Shift4 failed",
        nextStep:
          "Review the details below. If the error names a merchant field, fix it on the profile and submit again; otherwise pass the details to engineering.",
      };
  }
}

function ownerLabel(owner?: string): string | null {
  if (owner === "us") return "Us (environment / config)";
  if (owner === "merchant") return "Merchant data";
  if (owner === "vendor") return "Shift4";
  return owner || null;
}

export default function Shift4SubmitModal({
  visible,
  business,
  onClose,
  onSubmitted,
}: Shift4SubmitModalProps) {
  const [step, setStep] = useState<Step>("confirm");
  const [error, setError] = useState<Shift4SubmitError | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleClose = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setStep("confirm");
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!business) return;
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setStep("loading");
    try {
      await submitBusinessToShift4Server({
        businessId: business._id,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      // The row has to refresh on the submit itself, not on Done — closing with
      // the X would otherwise leave Submit clickable on a merchant already sent.
      onSubmitted?.(business._id);
      setStep("success");
    } catch (err) {
      if (controller.signal.aborted || axios.isCancel(err)) return;
      setError(err as Shift4SubmitError);
      setStep("error");
    }
  };

  const handleDone = () => {
    handleClose();
  };

  const handleCopyErrorDetails = () => {
    if (!error || !business) return;
    const lines = [
      `Merchant: ${business.businessName}`,
      `Business ID: ${business._id}`,
      `Error code: ${error.code ?? "-"}`,
      `HTTP: ${error.statusCode ?? "-"}`,
      `Message: ${error.message}`,
      error.name ? `Error name: ${error.name}` : null,
      error.failureOwner ? `Owner: ${error.failureOwner}` : null,
    ].filter(Boolean);
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      message.success("Copied error details");
    });
  };

  useEffect(() => {
    if (!visible || !business) return;
    setError(null);
    setStep("confirm");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, business?._id]);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const copy = errorCopy(error?.code);
  const canRetry = RETRYABLE_CODES.has(error?.code ?? "");
  const isConfirm = step === "confirm";
  const owner = ownerLabel(error?.failureOwner);

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      centered
      width={520}
      wrapClassName={styles.modal}
      title={isConfirm ? `Submit ${business?.businessName} to Shift4?` : null}
      closable={step !== "loading"}
      maskClosable={step !== "loading"}
      footer={
        isConfirm
          ? [
              <button
                key="no"
                className={styles.confirm_btn_secondary}
                onClick={handleClose}
              >
                No
              </button>,
              <button
                key="yes"
                className={styles.confirm_btn_primary}
                onClick={handleSubmit}
              >
                Yes, submit
              </button>,
            ]
          : null
      }
    >
      {step === "loading" && (
        <div className={styles.loading_body}>
          <Spin size="large" />
          <p className={styles.loading_text}>
            Submitting <strong>{business?.businessName}</strong> to Shift4…
          </p>
        </div>
      )}

      {step === "confirm" && (
        <div className={styles.confirm_body}>
          <p className={styles.confirm_subtitle}>
            This creates the merchant at Shift4 and starts underwriting. Shift4
            acknowledges immediately, but the approval decision arrives by
            webhook and can take up to 4 hours.
          </p>
        </div>
      )}

      {step === "success" && (
        <div className={styles.success_body}>
          <div className={styles.success_icon}>
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              <circle
                cx="22"
                cy="22"
                r="20"
                stroke="#22C55E"
                strokeWidth="2.5"
              />
              <path
                d="M13 22L19 28L31 16"
                stroke="#22C55E"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className={styles.success_title}>Submitted to Shift4</p>
          <p className={styles.success_subtitle}>
            <strong>{business?.businessName}</strong> was accepted by Shift4.
            The status on the row will move on its own as Shift4's notifications
            arrive — no further action needed.
          </p>
          <button className={styles.done_btn} onClick={handleDone}>
            Done
          </button>
        </div>
      )}

      {step === "error" && (
        <div className={styles.error_body}>
          <div className={styles.error_icon}>
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              <circle
                cx="22"
                cy="22"
                r="20"
                stroke="#ef4444"
                strokeWidth="2.5"
              />
              <path
                d="M15 15L29 29M29 15L15 29"
                stroke="#ef4444"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p className={styles.error_title}>{copy.title}</p>
          <p className={styles.error_subtitle}>
            <strong>{business?.businessName}</strong> was not submitted to
            Shift4.
          </p>
          <div className={styles.error_card}>
            <div className={styles.error_card_header}>
              <span className={styles.error_card_label}>Shift4 response</span>
              <span className={styles.error_http_badge}>
                {error?.statusCode
                  ? `HTTP ${error.statusCode}`
                  : (error?.code ?? "Error")}
              </span>
            </div>
            <div className={styles.error_row}>
              <span className={styles.error_field_name}>Error code</span>
              <span className={styles.error_field_value}>
                {error?.code ?? "—"}
              </span>
            </div>
            <div className={styles.error_row}>
              <span className={styles.error_field_name}>Message</span>
              <span className={styles.error_field_value}>
                {error?.message ?? "—"}
              </span>
            </div>
            {owner && (
              <div className={styles.error_row}>
                <span className={styles.error_field_name}>Who owns it</span>
                <span className={styles.error_field_value}>{owner}</span>
              </div>
            )}
          </div>
          <div className={styles.note_box}>
            <p className={styles.note_text}>{copy.nextStep}</p>
          </div>
          <div className={styles.error_buttons}>
            <button
              className={styles.confirm_btn_secondary}
              onClick={handleCopyErrorDetails}
            >
              Copy error details
            </button>
            {canRetry ? (
              <button
                className={styles.confirm_btn_primary}
                onClick={handleSubmit}
              >
                Try again
              </button>
            ) : (
              <button
                className={styles.confirm_btn_primary}
                onClick={handleClose}
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
